import { animateDOM } from 'core'
import { z } from 'zod'
import { useRecordingState } from '.'

export namespace Comment {
  export const Model = z.object({
    comment: z.string(),
    commenter_name: z.string().nullable(),
    commenter_profile_picture_url: z.string().nullable(),
    seconds: z.number(),
    timestamp: z.coerce.date()
  })

  export const InputModel = z.object({
    comment: z.string(),
    seconds: z.number()
  })

  export type IModel = z.infer<typeof Model>
  export type IInputModel = z.infer<typeof InputModel>

  export function getList(uid: string) {
    return http.static.get(http.getURL(`/comments/${uid}/list`))
      .json((data: { comments: IModel[] }) => Model.array().parse(data.comments))
  }

  export function add(uid: string, data: IInputModel) {
    // Append access token if authorized
    const fetch = http.isAuthorized
      ? http.static.auth(`Bearer ${http.tokens.accessToken}`)
      : http.static

    return fetch.post(data, http.getURL(`/comments/${uid}/add`))
      .json(Model.parse)
  }
}

export const useComments = createSharedComposable(() => {
  const source = useRecordingState()

  const uid = computed(() => source.data.value?.audio.id)
  const enabled = computed(() => source.data.value?.settings.comments.enabled === true)
  const list = shallowRef<Comment.IModel[]>([])

  /** List of hightlighted comments */
  const highlightedList = shallowRef<Comment.IModel[]>([])

  const dialog = reactive({
    enabled: false,
    comments: [] as Comment.IModel[],

    /** Index of active comment */
    activeIndex: 0,

    /** New comment draft */
    draft: {
      time: 0,
      text: ''
    }
  })

  function updateList(input: Comment.IModel[]) {
    const data = Array.from(input)
    data.sort((a, b) => a.seconds - b.seconds)

    animateDOM(() => {
      list.value = data
    })
  }

  async function sync() {
    updateList(await Comment.getList(uid.value!))
  }

  async function add() {
    const entry: Comment.IInputModel = {
      comment: dialog.draft.text.trim(),
      seconds: dialog.draft.time
    }

    const result = await Comment.add(uid.value!, entry)
    updateList([result, ...list.value])

    // Clear
    dialog.draft.text = ''
  }

  function showDialog(time: number = 0, list: Comment.IModel[] = []) {
    dialog.comments = list
    dialog.enabled = true
    dialog.activeIndex = 0

    dialog.draft.time = time
    dialog.draft.text = ''
  }

  const setHighlightedComments = debounce(100, (list: Comment.IModel[]) => {
    highlightedList.value = list
  })

  onMounted(sync)

  return reactive({
    enabled,
    list,
    highlightedList,

    dialog,
    add,
    showDialog,
    setHighlightedComments
  })
})
